import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { businessFormOption, bussinessDefaultValues } from "../../lib/constants";
import { useCreateBussiness } from "../../lib/queries/queriesAndMutations";
import { legalBussinessShema, realBussinessShema } from "../../lib/validation";
import { handleErrorApi, transformBusinessData } from "../../utils/utils";
import SelectBankAccounts from "../main/loginzone/SelectComponents/SelectBankAccounts";
import SelectCategory from "../main/loginzone/SelectComponents/SelectCategory";
import Loader from "../shared/Loader";
import { CustomInput, DateInput, FileUploader, OptionInput, UplaodMenu } from "../ui";
import RegexCustomInput from "../ui/RegexCustomInput";

export default function BussinessForm({ type, data, bussiness_type, bussiness_id }) {
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const disabled = type === "detail";
  const bussinessData = useMemo(
    () => (!disabled ? {} : { ...data.business, account_id: data.account?.id }),
    [disabled, data],
  );

  const [activeUplaodMenu, setActiveUplaodMenu] = useState({
    title: "تصویر آواتار",
    value: "logo",
    active: true,
  });

  const [validationSchema, setValidationSchema] = useState(realBussinessShema);

  const navigate = useNavigate();
  const { mutateAsync: createBussiness, isSubmitting, isPending } = useCreateBussiness();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: bussinessDefaultValues,
    values: type === "add" ? null : bussinessData,
    disabled: disabled,
    resolver: zodResolver(validationSchema),
  });

  const is_real = type === "detail" ? bussiness_type === "real" : watch("is_real");
  const logo_pic_type = is_real ? "REAL_PROFILE" : "LEGAL_PROFILE";
  const license_pic_type = is_real ? "REAL_BUSINESS_LICENSE" : "LEGAL_BUSINESS_LICENSE";

  useEffect(() => {
    setValidationSchema(is_real ? realBussinessShema : legalBussinessShema);
  }, [is_real]);

  const processForm = useCallback(
    async (formData) => {
      const transform = transformBusinessData(formData);
      const type = formData.is_real ? "real" : "legal";
      try {
        const res = await createBussiness({ data: transform, type });
        if (res?.status === 200) {
          queryClient.invalidateQueries({
            queryKey: ["getSidebarBusinessList"],
          });
          toast.success("کسب و کار با موفقیت ایجاد گردید.");
          navigate("/business/list");
        }
      } catch (error) {
        handleErrorApi(error);
      }
    },
    [createBussiness, navigate, queryClient],
  );

  useEffect(() => {
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <div className=" w-full h-full  flex-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full  p-2 md:rounded-xl bg-white  ">
      {!disabled ? (
        <p className="text-title-lg-semibold"> ثبت کسب و کار جدید</p>
      ) : (
        <p className="text-title-lg-semibold"> مشخصات کسب وکار </p>
      )}
      <hr />
      <form
        onSubmit={handleSubmit(processForm)}
        className={`grid grid-cols-2 w-full gap-4  ${disabled === "approved" && "pointer-events-none"} `}
      >
        {!disabled && (
          <OptionInput
            reset={reset}
            control={control}
            register={register}
            options={businessFormOption}
            name={"is_real"}
            errors={errors.is_real}
            className={"w-full col-span-2 min-h-[52px] "}
          />
        )}

        <RegexCustomInput
          control={control}
          disabled={disabled}
          setValue={setValue}
          setError={setError}
          clearErrors={clearErrors}
          name={"fa_name"}
          label={"نام کسب و کار (فارسی)*"}
          placeholder={"نام کسب و کار به فارسی"}
          type={"text"}
          errors={errors.fa_name}
          className={""}
          regexPattern={/^[\u0600-\u06FF0-9\s]+$/}
          errorMessage={"فقط کاراکتر فارسی مجاز است"}
        />
        <RegexCustomInput
          control={control}
          disabled={disabled}
          setValue={setValue}
          setError={setError}
          clearErrors={clearErrors}
          name={"en_name"}
          label={"نام کسب وکار (انگلیسی)*"}
          placeholder={"نام کسب وکار به انگلیسی"}
          type={"text"}
          errors={errors.en_name}
          className={""}
          regexPattern={/^[a-zA-Z0-9?><;,{}[\]\-_+=!@#$%^&*|' ]*$/}
          errorMessage={"فقط کاراکتر انگلیسی مجاز است"}
        />

        <CustomInput
          control={control}
          disabled={disabled}
          name={"address"}
          label={"نشانی"}
          placeholder={"تهران،شهرآرا،خیابان سی و چهارم"}
          type={"text"}
          errors={errors.address}
          className={""}
        />

        <CustomInput
          control={control}
          disabled={disabled}
          name={"phone"}
          label={"تلفن ثابت"}
          placeholder={"********021"}
          type={"number"}
          errors={errors.phone}
        />

        <CustomInput
          control={control}
          disabled={disabled}
          name={"postal_code"}
          label={" کدپستی"}
          placeholder={"کد ۱۰ رقمی"}
          type={"number"}
          errors={errors.postal_code}
        />
        <CustomInput
          control={control}
          disabled={disabled}
          name={"tax_code"}
          label={`کد مالیاتی  ${is_real ? "(اختیاری)" : ""}`}
          placeholder={"کد 10 رقمی"}
          type={"number"}
          errors={errors.tax_code}
        />

        {!is_real && (
          <CustomInput
            control={control}
            disabled={disabled}
            name={"national_id"}
            label={"شناسه ملی"}
            placeholder={"شناسه ملی"}
            type={"number"}
            errors={errors.national_id}
          />
        )}

        {!is_real && (
          <CustomInput
            control={control}
            disabled={disabled}
            name={"register_code"}
            label={"شماره ثبت"}
            placeholder={"شماره ثبت"}
            type={"number"}
            errors={errors.register_code}
          />
        )}

        {!is_real && (
          <DateInput
            control={control}
            register={register}
            disabled={disabled}
            name={"register_date"}
            label={"تاریخ ثبت"}
            placeholder={"روز/ماه/سال"}
            type={"text"}
            errors={errors.register_date}
            className={""}
            maxDate={tomorrow}
          />
        )}

        <SelectBankAccounts
          control={control}
          label={" حساب بانکی"}
          name={"account_id"}
          errors={errors.account_id}
          disabled={disabled}
        />

        <SelectCategory
          control={control}
          label={"صنف"}
          name={"category_id"}
          errors={errors.category_id}
          disabled={disabled}
        />

        <div className=" col-span-2 flex flex-col gap-4">
          <UplaodMenu setActiveUplaodMenu={setActiveUplaodMenu} type={"business"} value={is_real} />
          {activeUplaodMenu.value === "logo" && (
            <Controller
              control={control}
              name={"logo"}
              render={({ field: { onChange, value } }) => (
                <FileUploader
                  type={type}
                  bussiness_type={bussiness_type}
                  bussiness_id={bussiness_id}
                  pic_type={logo_pic_type}
                  emptyAvatar={true}
                  fieldChange={onChange}
                  mediaUrl={value}
                  disabled={disabled}
                  isEditable={true}
                  acceptedFormats={[".png", ".jpeg", ".jpg", ".webp"]}
                />
              )}
            />
          )}
          {activeUplaodMenu.value === "business_license_image" && (
            <Controller
              control={control}
              name={"business_license_image"}
              render={({ field: { onChange, value } }) => (
                <FileUploader
                  type={type}
                  bussiness_type={bussiness_type}
                  bussiness_id={bussiness_id}
                  emptyAvatar={true}
                  pic_type={license_pic_type}
                  fieldChange={onChange}
                  mediaUrl={value}
                  disabled={disabled}
                  acceptedFormats={[".png", ".jpeg", ".jpg", ".webp"]}
                />
              )}
            />
          )}
          {activeUplaodMenu.value === "announcement_image" && (
            <Controller
              control={control}
              name={"announcement_image"}
              render={({ field: { onChange, value } }) => (
                <FileUploader
                  type={type}
                  bussiness_type={bussiness_type}
                  bussiness_id={bussiness_id}
                  emptyAvatar={true}
                  pic_type={"ANNOUNCEMENT"}
                  fieldChange={onChange}
                  mediaUrl={value}
                  disabled={disabled}
                  acceptedFormats={[".png", ".jpeg", ".jpg", ".webp"]}
                />
              )}
            />
          )}

          {activeUplaodMenu.value === "last_announcement_image" && (
            <Controller
              control={control}
              name={"last_announcement_image"}
              render={({ field: { onChange, value } }) => (
                <FileUploader
                  type={type}
                  bussiness_type={bussiness_type}
                  bussiness_id={bussiness_id}
                  pic_type={"LAST_ANNOUNCEMENT"}
                  emptyAvatar={true}
                  fieldChange={onChange}
                  mediaUrl={value}
                  disabled={disabled}
                  acceptedFormats={[".png", ".jpeg", ".jpg", ".webp"]}
                />
              )}
            />
          )}

          {["logo", "announcement_image", "last_announcement_image"].map((field) =>
            errors[field]?.preview_url?.message ? (
              <span key={field} className="text-red-500 text-sm">
                {errors[field].preview_url.message}
              </span>
            ) : null,
          )}
        </div>

        {!disabled && (
          <button
            disabled={disabled || isPending}
            type="submit"
            className="btn-container disabled:bg-neutral-40 md:min-w-[400px] md:mr-auto    "
          >
            {isSubmitting || isPending ? (
              <div className="flex gap-4 items-center">
                <p className="text-xs">در حال ارسال</p>
                <div className="flex-center gap-2 w-6 h-full ">
                  <Loader />
                </div>
              </div>
            ) : (
              <p className="text-title-lg-regular "> ثبت کسب و کار جدید</p>
            )}
          </button>
        )}
      </form>
    </div>
  );
}
